import { useState } from "react";
import { Facebook, Linkedin, Link2, Mail, Check } from "lucide-react";

type Props = {
  url: string;
  title: string;
  className?: string;
};

const SITE_ORIGIN = "https://capitalpropressurewashing.com";

/** Always share the public production URL, never a preview or localhost URL. */
function toCanonicalUrl(value: string) {
  try {
    const parsed = new URL(value, SITE_ORIGIN);
    return `${SITE_ORIGIN}${parsed.pathname}${parsed.search}`;
  } catch {
    return SITE_ORIGIN;
  }
}

const btn =
  "inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white text-[var(--brand-deep-blue)] transition-colors hover:bg-[var(--brand-light-gray)]";

/**
 * Social share dialogs are blocked as pop-unders in some browsers when opened
 * as a plain target="_blank" link, so open them as an explicit popup window
 * and fall back to the native share sheet / new tab.
 */
function openShare(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  e.preventDefault();
  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;
  // On phones a sized popup is unreliable — open the sharer in a normal tab.
  if (isTouch) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }
  const w = 620;
  const h = 640;
  const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
  const popup = window.open(
    href,
    "share-dialog",
    `noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`,
  );
  if (!popup) window.location.href = href;
}

export function ShareButtons({ url, title, className = "" }: Props) {
  const [copied, setCopied] = useState(false);
  const canonicalUrl = toCanonicalUrl(url);
  const u = encodeURIComponent(canonicalUrl);
  const t = encodeURIComponent(title);
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/60">
        Share
      </span>
      <a
        className={btn}
        href={facebookUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
      >
        <Facebook size={18} />
      </a>
      <a
        className={btn}
        href={`https://twitter.com/intent/tweet?url=${u}&text=${t}`}
        onClick={(e) => openShare(e, `https://twitter.com/intent/tweet?url=${u}&text=${t}`)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.9l-4.5-5.86L5.9 22H2.64l7.98-9.12L1.5 2h7.07l4.07 5.38L18.244 2Zm-1.21 18h1.8L7.05 3.9H5.12L17.034 20Z" />
        </svg>
      </a>
      <a
        className={btn}
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`}
        onClick={(e) =>
          openShare(e, `https://www.linkedin.com/sharing/share-offsite/?url=${u}`)
        }
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
      >
        <Linkedin size={18} />
      </a>
      <a
        className={btn}
        href={`mailto:?subject=${t}&body=${u}`}
        aria-label="Share by email"
      >
        <Mail size={18} />
      </a>
      <button type="button" onClick={copy} className={btn} aria-label="Copy link">
        {copied ? <Check size={18} /> : <Link2 size={18} />}
      </button>
      <span aria-live="polite" className="sr-only">
        {copied ? "Link copied" : ""}
      </span>
    </div>
  );
}